import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../../../lib/api";
import { toast } from "sonner";

const fmtAge = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

const ageBadgeClass = (m) => {
  if (m == null) return "bg-[#4A5C50]";
  if (m < 30) return "bg-[#CBA35C]";   // recent — gold
  if (m < 60 * 6) return "bg-[#D96C4A]"; // hours — orange
  return "bg-[#B33A3A]";                 // 6h+ — red, needs admin attention
};

export function ReconcileStuckCardsPanel() {
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadPreview = useCallback(async () => {
    setPreviewBusy(true);
    try {
      const r = await api.get("/admin/stripe-stuck-preview");
      setPreview(r.data);
    } catch (e) {
      // Soft fail — the panel still works without the preview.
    } finally {
      setPreviewBusy(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/reconcile-stuck-cards");
      setResult(r.data);
      const s = r.data?.summary || {};
      toast.success(
        `Finalized ${s.finalized || 0} · Credited ${s.credited_topups || 0} topups · ${s.orphaned_paid || 0} orphan paid sessions`
      );
      // Refresh the preview list once we've reconciled.
      await loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusy(false);
    }
  };

  const stuckCount = preview?.counts?.cards || 0;

  return (
    <div className="mt-5 rounded-xl bg-[#112318] text-[#FBF9F6] p-5" data-testid="reconcile-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-3">
            <p className="serif text-lg">Recover stuck Stripe payments</p>
            {!previewBusy && (
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${stuckCount > 0 ? "bg-[#D96C4A] text-white" : "bg-[#4A5C50] text-[#FBF9F6]"}`}
                data-testid="reconcile-stuck-count"
              >
                {stuckCount} stuck
              </span>
            )}
          </div>
          <p className="text-xs opacity-70 mt-1 leading-relaxed max-w-xl">
            If a webhook couldn't reach the backend (e.g., backend crash during deploy,
            misconfigured Stripe webhook URL, signature mismatch), some cards may be stuck
            in <span className="mono">pending_payment</span> even though the user paid.
            This scans every stuck card/topup, queries Stripe, and finalizes anything
            already paid. Safe to run repeatedly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadPreview}
            disabled={previewBusy}
            className="px-3 py-2 rounded-lg border border-[#FBF9F6]/30 hover:border-[#FBF9F6]/60 text-[#FBF9F6] text-xs disabled:opacity-50"
            data-testid="reconcile-refresh-btn"
          >
            {previewBusy ? "…" : "Refresh"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={run}
            className="px-4 py-2 rounded-lg bg-[#CBA35C] hover:bg-[#B0883E] text-[#112318] text-sm font-medium disabled:opacity-50"
            data-testid="reconcile-run-btn"
          >
            {busy ? "Reconciling…" : "Reconcile now"}
          </button>
        </div>
      </div>

      {/* Stuck preview — live list of cards currently waiting */}
      {preview && preview.pending_cards?.length > 0 && (
        <div className="mt-4 rounded-lg bg-[#0F1F18] border border-[#FBF9F6]/10 overflow-hidden" data-testid="stuck-preview">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest opacity-60 border-b border-[#FBF9F6]/10">
            Currently stuck · click "Reconcile now" to attempt finalization
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest opacity-70">
              <tr>
                <th className="text-left p-2.5">Age</th>
                <th className="text-left p-2.5">Receiver</th>
                <th className="text-left p-2.5">Kind</th>
                <th className="text-left p-2.5">Source</th>
                <th className="text-right p-2.5">Amount</th>
                <th className="text-left p-2.5 mono">Session</th>
              </tr>
            </thead>
            <tbody>
              {preview.pending_cards.map((c) => (
                <tr key={c.id} className="border-t border-[#FBF9F6]/5" data-testid={`stuck-card-row-${c.id}`}>
                  <td className="p-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] text-white ${ageBadgeClass(c.age_minutes)}`}>
                      {fmtAge(c.age_minutes)}
                    </span>
                  </td>
                  <td className="p-2.5">
                    {c.receiver_name ? <div className="font-semibold">{c.receiver_name}</div> : null}
                    {c.receiver_email && <div className="opacity-60 mono text-[10px]">{c.receiver_email}</div>}
                  </td>
                  <td className="p-2.5">
                    <span className="opacity-70 capitalize">{(c.kind || "customer").replace("_", " ")}</span>
                  </td>
                  <td className="p-2.5 opacity-70">
                    {c.issued_by_agent_code ? `agent:${c.issued_by_agent_code}` : "customer"}
                  </td>
                  <td className="p-2.5 text-right mono">${(c.source_amount || 0).toFixed(2)}</td>
                  <td className="p-2.5 mono opacity-60">{(c.checkout_session_id || "").slice(0, 22)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && preview.pending_cards?.length === 0 && !busy && (
        <p className="mt-4 text-xs opacity-60" data-testid="reconcile-empty-state">
          ✓ No stuck cards right now. Stripe webhook and reconciliation are healthy.
        </p>
      )}

      {/* Last reconcile result */}
      {result && (
        <div className="mt-4 text-xs opacity-90 space-y-2" data-testid="reconcile-result">
          <p className="mono">
            Last run: scanned {result.summary?.scanned_cards || 0} cards, {result.summary?.scanned_topups || 0} topups
          </p>
          {result.finalized_cards?.length > 0 && (
            <div>
              <p className="font-semibold text-[#CBA35C]">Finalized {result.finalized_cards.length}:</p>
              <ul className="mono ml-4 mt-1 space-y-0.5">
                {result.finalized_cards.map((c) => (
                  <li key={c.card_id}>• {c.receiver_name} · ••••{c.last4} · ${c.balance_vnd}</li>
                ))}
              </ul>
            </div>
          )}
          {result.credited_topups?.length > 0 && (
            <div>
              <p className="font-semibold text-[#8BA888]">Credited {result.credited_topups.length} topups:</p>
              <ul className="mono ml-4 mt-1 space-y-0.5">
                {result.credited_topups.map((t) => (
                  <li key={t.session_id}>• card {t.card_id.slice(0, 8)} · +${t.amount_vnd}</li>
                ))}
              </ul>
            </div>
          )}
          {result.orphaned_paid_sessions?.length > 0 && (
            <div>
              <p className="font-semibold text-[#D96C4A]">{result.orphaned_paid_sessions.length} orphan paid session(s) — Stripe received payment but no matching card in DB. Manual attention needed:</p>
              <ul className="mono ml-4 mt-1 space-y-0.5">
                {result.orphaned_paid_sessions.map((s) => (
                  <li key={s.session_id}>• ${s.amount_usd} · {s.email || "no-email"} · {s.session_id.slice(0, 25)}…</li>
                ))}
              </ul>
            </div>
          )}
          {result.summary?.skipped > 0 && (
            <p className="opacity-60">Skipped {result.summary.skipped} (unpaid / abandoned / other — see server logs for details)</p>
          )}
        </div>
      )}
    </div>
  );
}
